import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { ArrowUpRight, CheckCircle2, XCircle, Clock, RefreshCw, Eye, Search, ExternalLink, Copy } from "lucide-react";

interface Withdrawal {
  id: string;
  user_id: string;
  crypto_symbol: string;
  crypto_name: string;
  amount: number;
  wallet_address: string;
  network: string;
  status: string;
  admin_notes: string | null;
  tx_hash: string | null;
  created_at: string;
}

const AdminWithdrawals = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [txHash, setTxHash] = useState("");
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Explorer URLs per network
  const getExplorerUrl = (network: string, txHash: string): string | null => {
    const explorers: Record<string, string> = {
      "Ethereum (ERC20)": `https://etherscan.io/tx/${txHash}`,
      "BSC (BEP20)": `https://bscscan.com/tx/${txHash}`,
      "Polygon": `https://polygonscan.com/tx/${txHash}`,
      "Arbitrum": `https://arbiscan.io/tx/${txHash}`,
      "Optimism": `https://optimistic.etherscan.io/tx/${txHash}`,
      "Base": `https://basescan.org/tx/${txHash}`,
      "Avalanche C-Chain": `https://snowtrace.io/tx/${txHash}`,
      "Bitcoin": `https://mempool.space/tx/${txHash}`,
      "Litecoin": `https://blockchair.com/litecoin/transaction/${txHash}`,
      "Tron (TRC20)": `https://tronscan.org/#/transaction/${txHash}`,
      "Solana": `https://solscan.io/tx/${txHash}`,
    };
    return explorers[network] || null;
  };

  const getAddressExplorerUrl = (network: string, address: string): string | null => {
    const explorers: Record<string, string> = {
      "Ethereum (ERC20)": `https://etherscan.io/address/${address}`,
      "BSC (BEP20)": `https://bscscan.com/address/${address}`,
      "Polygon": `https://polygonscan.com/address/${address}`,
      "Arbitrum": `https://arbiscan.io/address/${address}`,
      "Optimism": `https://optimistic.etherscan.io/address/${address}`,
      "Base": `https://basescan.org/address/${address}`,
      "Avalanche C-Chain": `https://snowtrace.io/address/${address}`,
      "Bitcoin": `https://mempool.space/address/${address}`,
      "Litecoin": `https://blockchair.com/litecoin/address/${address}`,
      "Tron (TRC20)": `https://tronscan.org/#/address/${address}`,
      "Solana": `https://solscan.io/account/${address}`,
    };
    return explorers[network] || null;
  };

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("withdrawal_requests")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setWithdrawals((data || []) as Withdrawal[]);
    } catch (error) {
      console.error("Error fetching withdrawals:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action: "approved" | "rejected") => {
    if (!selectedWithdrawal || !user) return;
    setProcessing(true);
    try {
      const updateData: Record<string, any> = {
        status: action,
        admin_notes: adminNotes || null,
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
      };
      if (action === "approved" && txHash) {
        updateData.tx_hash = txHash;
      }

      const { error } = await supabase
        .from("withdrawal_requests")
        .update(updateData)
        .eq("id", selectedWithdrawal.id);

      if (error) throw error;

      toast({
        title: action === "approved" ? "تمت الموافقة" : "تم الرفض",
        description: action === "approved" ? "تمت الموافقة على طلب السحب" : "تم رفض طلب السحب وإعادة الرصيد",
      });

      // Send email notification
      supabase.functions.invoke("notify-transaction", {
        body: {
          type: "withdrawal",
          userId: selectedWithdrawal.user_id,
          amount: selectedWithdrawal.amount,
          cryptoSymbol: selectedWithdrawal.crypto_symbol,
          transactionNumber: (selectedWithdrawal as any).transaction_number,
          action,
        },
      }).catch(e => console.error("Email notification error:", e));

      setSelectedWithdrawal(null);
      setAdminNotes("");
      setTxHash("");
      fetchWithdrawals();
    } catch (error: any) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } finally {
      setProcessing(false);
    }
  };

  const filteredWithdrawals = (() => {
    let list = filter === "all" ? withdrawals : withdrawals.filter(w => w.status === filter);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(w =>
        w.tx_hash?.toLowerCase().includes(q) ||
        (w as any).transaction_number?.toLowerCase().includes(q) ||
        w.wallet_address.toLowerCase().includes(q) ||
        w.crypto_symbol.toLowerCase().includes(q) ||
        w.user_id.toLowerCase().includes(q)
      );
    }
    return list;
  })();
  const pendingCount = withdrawals.filter(w => w.status === "pending").length;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending": return <Badge variant="outline" className="text-yellow-500 border-yellow-500"><Clock className="w-3 h-3 ml-1" />قيد المراجعة</Badge>;
      case "approved": return <Badge variant="outline" className="text-green-500 border-green-500"><CheckCircle2 className="w-3 h-3 ml-1" />تمت الموافقة</Badge>;
      case "rejected": return <Badge variant="outline" className="text-red-500 border-red-500"><XCircle className="w-3 h-3 ml-1" />مرفوض</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return <Card className="glass"><CardContent className="p-6"><div className="animate-pulse text-muted-foreground">جاري التحميل...</div></CardContent></Card>;
  }

  return (
    <div className="space-y-6">
      <Card className="glass">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-foreground">
              <ArrowUpRight className="w-5 h-5 text-primary" />
              إدارة طلبات السحب
              {pendingCount > 0 && (
                <Badge className="bg-yellow-500 text-white">{pendingCount} قيد الانتظار</Badge>
              )}
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchWithdrawals}><RefreshCw className="w-4 h-4" /></Button>
              {["all", "pending", "approved", "rejected"].map(f => (
                <Button
                  key={f}
                  variant={filter === f ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilter(f)}
                >
                  {f === "all" ? "الكل" : f === "pending" ? "قيد الانتظار" : f === "approved" ? "موافق" : "مرفوض"}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="mb-4 relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="ابحث بـ TX Hash أو رقم المعاملة أو العنوان..."
              className="pr-10 bg-secondary/60 border-0 rounded-xl"
              dir="ltr"
            />
          </div>
          {filteredWithdrawals.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">لا توجد طلبات سحب</p>
          ) : (
            <div className="space-y-3">
              {filteredWithdrawals.map(w => (
                <div key={w.id} className="p-4 rounded-xl bg-secondary/50 border border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-foreground">{w.amount} {w.crypto_symbol}</span>
                      {getStatusBadge(w.status)}
                    </div>
                    {(w as any).transaction_number && (
                      <p className="text-xs text-primary font-mono">#{(w as any).transaction_number}</p>
                    )}
                    <p className="text-sm text-muted-foreground">الشبكة: {w.network}</p>
                    <p className="text-xs text-muted-foreground font-mono truncate max-w-[300px]" dir="ltr">{w.wallet_address}</p>
                    {w.tx_hash && (
                      <p className="text-xs font-mono truncate max-w-[300px]" dir="ltr"><span className="text-muted-foreground">TxID:</span> <span className="text-green-500">{w.tx_hash}</span></p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {new Date(w.created_at).toLocaleDateString('ar-EG', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => { setSelectedWithdrawal(w); setAdminNotes(w.admin_notes || ""); setTxHash(w.tx_hash || ""); }}>
                    <Eye className="w-4 h-4 ml-1" />
                    تفاصيل
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedWithdrawal} onOpenChange={() => setSelectedWithdrawal(null)}>
        <DialogContent className="sm:max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle>تفاصيل طلب السحب</DialogTitle>
          </DialogHeader>
          {selectedWithdrawal && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">العملة:</span> <span className="font-semibold text-foreground">{selectedWithdrawal.crypto_name} ({selectedWithdrawal.crypto_symbol})</span></div>
                <div><span className="text-muted-foreground">المبلغ:</span> <span className="font-semibold text-foreground">{selectedWithdrawal.amount}</span></div>
                <div><span className="text-muted-foreground">الشبكة:</span> <span className="font-semibold text-foreground">{selectedWithdrawal.network}</span></div>
                <div><span className="text-muted-foreground">الحالة:</span> {getStatusBadge(selectedWithdrawal.status)}</div>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">عنوان المحفظة:</span>
                <div className="flex items-center gap-2 mt-1">
                  <p className="font-mono text-sm text-foreground break-all bg-secondary p-2 rounded-lg flex-1" dir="ltr">{selectedWithdrawal.wallet_address}</p>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { navigator.clipboard.writeText(selectedWithdrawal.wallet_address); toast({ title: "تم النسخ" }); }}>
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    {getAddressExplorerUrl(selectedWithdrawal.network, selectedWithdrawal.wallet_address) && (
                      <a href={getAddressExplorerUrl(selectedWithdrawal.network, selectedWithdrawal.wallet_address)!} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="فحص العنوان على المستكشف">
                          <ExternalLink className="w-3.5 h-3.5 text-primary" />
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {(selectedWithdrawal as any).transaction_number && (
                <div>
                  <span className="text-sm text-muted-foreground">رقم المعاملة:</span>
                  <p className="font-mono text-sm text-primary font-semibold mt-1">#{(selectedWithdrawal as any).transaction_number}</p>
                </div>
              )}

              {selectedWithdrawal.status === "pending" && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">TX Hash (اختياري)</label>
                    <Input value={txHash} onChange={e => setTxHash(e.target.value)} placeholder="0x..." dir="ltr" className="font-mono text-sm bg-input" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">ملاحظات</label>
                    <Textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="ملاحظات اختيارية..." className="bg-input" />
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={() => handleAction("approved")} disabled={processing} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                      <CheckCircle2 className="w-4 h-4 ml-1" /> موافقة
                    </Button>
                    <Button onClick={() => handleAction("rejected")} disabled={processing} variant="destructive" className="flex-1">
                      <XCircle className="w-4 h-4 ml-1" /> رفض
                    </Button>
                  </div>
                </>
              )}

              {selectedWithdrawal.tx_hash && (
                <div>
                  <span className="text-sm text-muted-foreground">TX Hash:</span>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="font-mono text-sm text-green-500 break-all flex-1" dir="ltr">{selectedWithdrawal.tx_hash}</p>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { navigator.clipboard.writeText(selectedWithdrawal.tx_hash!); toast({ title: "تم النسخ" }); }}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      {getExplorerUrl(selectedWithdrawal.network, selectedWithdrawal.tx_hash) && (
                        <a href={getExplorerUrl(selectedWithdrawal.network, selectedWithdrawal.tx_hash)!} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="فحص المعاملة على المستكشف">
                            <ExternalLink className="w-3.5 h-3.5 text-primary" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {selectedWithdrawal.admin_notes && selectedWithdrawal.status !== "pending" && (
                <div>
                  <span className="text-sm text-muted-foreground">ملاحظات الإدارة:</span>
                  <p className="text-sm text-foreground mt-1">{selectedWithdrawal.admin_notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminWithdrawals;
